/**
 * ProductsLens — product selection with a clear view/edit mode split.
 *
 * View Mode (default when products are resolved):
 *   Full-width gallery showing the confirmed resolved products.
 *   Header shows count + strategy pill + "Change Products" or "Unlock to Change".
 *
 * Edit Mode (auto-opened on new briefs, triggered by "Change Products"):
 *   Left: mode selector + config controls.
 *   Right: live product browser.
 *   "Confirm Selection" resolves + locks and returns to View Mode.
 *   "Cancel" returns to View Mode without saving pending changes.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  AlertTriangle,
  BarChart2,
  Check,
  Lock,
  Package,
  Pencil,
  Search,
  Sparkles,
  Tag,
  TrendingUp,
  Unlock,
  X,
} from 'lucide-react';
import clsx from 'clsx';
import {
  generationApi,
  productCatalogApi,
  type CatalogProduct,
  type GenerationBrief,
  type ProductConfiguration,
  type ProductFilterRules,
  type ProductSelectionMode,
  type ProductSortMetric,
} from '@/services/api';

interface Props {
  brief: GenerationBrief;
  /** Called after every successful config mutation so the parent can sync the store. */
  onConfigChange?: (config: ProductConfiguration) => void;
}

const DEFAULT_CONFIG: ProductConfiguration = {
  mode: 'top_n',
  top_n: 5,
  sort_by: 'revenue',
  lookback_days: 90,
  locked: false,
};

const SORT_LABEL: Record<string, string> = {
  revenue: 'Revenue',
  conversion_rate: 'Conv. Rate',
  views: 'Views',
};

function strategyPill(config: ProductConfiguration): string {
  if (config.mode === 'top_n') {
    const n = config.top_n ?? 5;
    const sort = SORT_LABEL[config.sort_by ?? 'revenue'] ?? config.sort_by;
    const days = config.lookback_days ?? 90;
    return `Top ${n} · ${sort} · ${days}d`;
  }
  if (config.mode === 'explicit') {
    const count = config.product_ids?.length ?? 0;
    return `${count} hand-picked`;
  }
  if (config.mode === 'rule_based') {
    const ruleCount = Object.values(config.rules ?? {}).filter((v) =>
      Array.isArray(v) ? v.length > 0 : v != null,
    ).length;
    return `Rule-based · ${ruleCount} filter${ruleCount !== 1 ? 's' : ''}`;
  }
  return 'Custom';
}

export function ProductsLens({ brief, onConfigChange }: Props) {
  const [config, setConfig] = useState<ProductConfiguration>(
    brief.product_config ?? DEFAULT_CONFIG,
  );
  const [saving, setSaving] = useState(false);
  const [resolving, setResolving] = useState(false);
  const [locking, setLocking] = useState(false);
  const [unlocking, setUnlocking] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // View/Edit mode: start in edit mode if nothing is resolved yet.
  const hasResolved = (config.resolved_product_ids?.length ?? 0) > 0;
  const [editMode, setEditMode] = useState(!hasResolved);

  useEffect(() => {
    const next = brief.product_config ?? DEFAULT_CONFIG;
    setConfig(next);
    // If the incoming brief now has resolved products, exit edit mode.
    if ((next.resolved_product_ids?.length ?? 0) > 0) {
      setEditMode(false);
    }
  }, [brief.brief_id, brief.version]);

  const orgUid = brief.organization_id ?? brief.client_uid;

  const applyConfig = useCallback(
    (next: ProductConfiguration) => {
      setConfig(next);
      onConfigChange?.(next);
    },
    [onConfigChange],
  );

  const patch = useCallback(
    async (updates: Partial<ProductConfiguration>) => {
      const next = { ...config, ...updates };
      applyConfig(next);
      setError(null);
      setSaving(true);
      try {
        await generationApi.updateBrief(brief.brief_id, { product_config: next });
      } catch (e) {
        setError(String(e));
      } finally {
        setSaving(false);
      }
    },
    [config, brief.brief_id, applyConfig],
  );

  const handleModeChange = (mode: ProductSelectionMode) => {
    const next: ProductConfiguration = {
      ...config,
      mode,
      product_ids: mode === 'explicit' ? config.product_ids ?? [] : undefined,
      rules: mode === 'rule_based' ? config.rules ?? {} : undefined,
      locked: false,
      locked_at: undefined,
    };
    applyConfig(next);
    generationApi.updateBrief(brief.brief_id, { product_config: next }).catch(() => {});
  };

  const runResolve = async (): Promise<ProductConfiguration> => {
    setError(null);
    setResolving(true);
    try {
      await generationApi.updateBrief(brief.brief_id, { product_config: config });
      const r = await generationApi.resolveProducts(brief.brief_id);
      const next = { ...config, resolved_product_ids: r.resolved_product_ids, resolved_at: r.resolved_at };
      applyConfig(next);
      return next;
    } finally {
      setResolving(false);
    }
  };

  const runLock = async (resolvedConfig?: ProductConfiguration) => {
    setError(null);
    setLocking(true);
    try {
      const b = await generationApi.lockProducts(brief.brief_id);
      if (b.product_config) applyConfig(b.product_config);
    } finally {
      setLocking(false);
    }
    void resolvedConfig; // suppress unused warning
  };

  const runUnlock = async () => {
    setError(null);
    setUnlocking(true);
    try {
      const next: ProductConfiguration = { ...config, locked: false, locked_at: undefined };
      const b = await generationApi.updateBrief(brief.brief_id, { product_config: next });
      applyConfig(b.product_config ?? next);
    } catch (e) {
      setError(String(e));
    } finally {
      setUnlocking(false);
    }
  };

  /** Confirm: resolve products, lock them, then switch to view mode. */
  const runConfirm = async () => {
    setError(null);
    setConfirming(true);
    try {
      const resolved = await runResolve();
      await runLock(resolved);
      setEditMode(false);
    } catch (e) {
      setError(String(e));
    } finally {
      setConfirming(false);
    }
  };

  const canConfirm =
    config.mode === 'top_n' ||
    (config.mode === 'explicit' && (config.product_ids?.length ?? 0) > 0) ||
    (config.mode === 'rule_based' && Object.keys(config.rules ?? {}).length > 0);

  // ── Active brief warning (shown in edit mode regardless)
  const activeBriefBanner = brief.status === 'active' && !config.locked && (
    <div className="mx-6 mt-4 flex items-center gap-2 bg-amber-50 border border-amber-200 text-amber-800 rounded-xl px-4 py-2.5 text-xs">
      <AlertTriangle size={12} className="shrink-0 mt-px" />
      <span>
        This brief is active. Changes to product selection will be applied immediately.
      </span>
    </div>
  );

  // ── View Mode ─────────────────────────────────────────────────────────────
  if (!editMode && hasResolved) {
    return (
      <div className="flex flex-col h-full">
        {activeBriefBanner}
        <SelectedProductsView
          config={config}
          clientUid={brief.client_uid}
          orgUid={orgUid}
          onEdit={() => {
            if (config.locked) {
              runUnlock().then(() => setEditMode(true));
            } else {
              setEditMode(true);
            }
          }}
          locked={config.locked}
          unlocking={unlocking}
        />
        {error && (
          <div className="mx-6 mt-2 text-xs text-red-700 bg-red-50 border border-red-200 rounded-lg px-3 py-2">
            {error}
          </div>
        )}
      </div>
    );
  }

  // ── Edit Mode ─────────────────────────────────────────────────────────────
  return (
    <div className="flex flex-col h-full">
      {activeBriefBanner}

      {/* Main two-column layout */}
      <div className="flex-1 grid lg:grid-cols-[360px_1fr] gap-0 min-h-0 overflow-hidden">
        {/* Left: configurator */}
        <div className="border-r border-slate-200 overflow-y-auto p-6 space-y-5">
          <div>
            <h2 className="text-base font-bold text-slate-900">Target Products</h2>
            <p className="text-xs text-slate-500 mt-0.5">
              Define which products guide this brief's generated assets.
            </p>
          </div>

          {/* Mode selector */}
          <ModeSelector mode={config.mode} onChange={handleModeChange} />

          {/* Mode-specific config */}
          {config.mode === 'top_n' && (
            <TopNConfig config={config} onChange={patch} />
          )}
          {config.mode === 'explicit' && (
            <ExplicitConfig
              config={config}
              onChange={patch}
              clientUid={brief.client_uid}
              orgUid={orgUid}
            />
          )}
          {config.mode === 'rule_based' && (
            <RuleBasedConfig
              config={config}
              onChange={patch}
              clientUid={brief.client_uid}
              orgUid={orgUid}
            />
          )}

          {error && (
            <div className="text-xs text-red-700 bg-red-50 border border-red-200 rounded-lg px-3 py-2">
              {error}
            </div>
          )}

          {/* Confirm + Cancel */}
          <div className="pt-2 border-t border-slate-100 space-y-2">
            {!canConfirm && config.mode === 'rule_based' && (
              <p className="text-[11px] text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
                Configure at least one rule before confirming.
              </p>
            )}
            {!canConfirm && config.mode === 'explicit' && (
              <p className="text-[11px] text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
                Select at least one product before confirming.
              </p>
            )}
            <div className="flex items-center gap-3">
              <button
                onClick={runConfirm}
                disabled={confirming || resolving || locking || saving || !canConfirm}
                title={!canConfirm ? 'Configure products first' : undefined}
                className="flex items-center gap-1.5 px-3 py-2 text-xs font-semibold text-white bg-teal-500 rounded-lg hover:bg-teal-600 disabled:opacity-40 transition"
              >
                {confirming || resolving || locking ? (
                  <span className="w-3 h-3 border-2 border-white/40 border-t-white rounded-full animate-spin" />
                ) : (
                  <Lock size={12} />
                )}
                Confirm Selection
              </button>
              {hasResolved && (
                <button
                  onClick={() => setEditMode(false)}
                  disabled={confirming}
                  className="text-xs text-slate-400 hover:text-slate-600 transition"
                >
                  Cancel
                </button>
              )}
              {saving && <span className="text-[11px] text-slate-400">Saving…</span>}
            </div>
          </div>

          {config.resolved_at && (
            <p className="text-[10px] text-slate-400">
              Last resolved {new Date(config.resolved_at).toLocaleString()}
            </p>
          )}
        </div>

        {/* Right: live product browser */}
        <ProductBrowser
          config={config}
          onConfigChange={applyConfig}
          briefId={brief.brief_id}
          clientUid={brief.client_uid}
          orgUid={orgUid}
        />
      </div>
    </div>
  );
}

/* ─── Selected Products View (View Mode) ─────────────────────────────────── */

function SelectedProductsView({
  config,
  clientUid,
  orgUid,
  onEdit,
  locked,
  unlocking,
}: {
  config: ProductConfiguration;
  clientUid: string;
  orgUid: string;
  onEdit: () => void;
  locked: boolean;
  unlocking: boolean;
}) {
  const [products, setProducts] = useState<CatalogProduct[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);

  const resolvedIds = useMemo(
    () => new Set(config.resolved_product_ids ?? []),
    [config.resolved_product_ids],
  );
  const count = resolvedIds.size;

  useEffect(() => {
    if (count === 0) return;
    let cancelled = false;
    setLoading(true);
    setLoadError(null);

    const fetchProducts = async () => {
      try {
        if (config.mode === 'top_n') {
          const r = await productCatalogApi.topNProducts({
            client_uid: clientUid,
            org_uid: orgUid,
            n: config.top_n ?? 5,
            sort_by: (config.sort_by ?? 'revenue') as 'revenue' | 'conversion_rate' | 'views',
          });
          if (!cancelled) setProducts(r.products);
        } else {
          // For explicit / rule_based: fetch a broad search and filter to resolved IDs.
          const r = await productCatalogApi.searchProducts({
            client_uid: clientUid,
            org_uid: orgUid,
            limit: 200,
            offset: 0,
          });
          if (!cancelled) {
            setProducts(r.products.filter((p) => resolvedIds.has(p.product_id)));
          }
        }
      } catch (e) {
        if (!cancelled) setLoadError(String(e));
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    fetchProducts();
    return () => { cancelled = true; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [config.mode, config.top_n, config.sort_by, clientUid, orgUid, config.resolved_product_ids?.join(',')]);

  return (
    <div className="flex flex-col flex-1 min-h-0 overflow-hidden">
      {/* Header */}
      <div className="px-6 py-4 border-b border-slate-200 flex items-center gap-3">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <h3 className="text-sm font-bold text-slate-900">
              {count} Product{count !== 1 ? 's' : ''} Selected
            </h3>
            {locked && (
              <span className="flex items-center gap-1 text-[10px] font-semibold text-teal-700 bg-teal-50 border border-teal-200 rounded-full px-2 py-0.5">
                <Lock size={9} /> Locked
              </span>
            )}
          </div>
          <p className="text-xs text-slate-400 mt-0.5">{strategyPill(config)}</p>
        </div>
        <button
          onClick={onEdit}
          disabled={unlocking}
          className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold text-slate-600 border border-slate-200 rounded-lg hover:border-slate-300 hover:bg-slate-50 disabled:opacity-50 transition"
        >
          {unlocking ? (
            <span className="w-3 h-3 border-2 border-slate-300 border-t-slate-600 rounded-full animate-spin" />
          ) : locked ? (
            <Unlock size={12} />
          ) : (
            <Pencil size={12} />
          )}
          {locked ? 'Unlock to Change' : 'Change Products'}
        </button>
      </div>

      {/* Product grid */}
      <div className="flex-1 overflow-y-auto px-6 py-4">
        {loading && (
          <div className="flex items-center gap-2 text-sm text-slate-500 py-6">
            <span className="w-4 h-4 border-2 border-slate-200 border-t-teal-500 rounded-full animate-spin" />
            Loading products…
          </div>
        )}
        {loadError && (
          <div className="text-xs text-red-700 bg-red-50 border border-red-200 rounded-lg px-3 py-2">
            {loadError}
          </div>
        )}
        {!loading && !loadError && products.length === 0 && (
          <div className="flex flex-col items-center justify-center py-12 text-slate-400">
            <Package size={28} className="mb-2 opacity-40" />
            <p className="text-sm font-medium">No product details available</p>
            <p className="text-xs mt-1">The resolved IDs could not be matched in the catalog.</p>
          </div>
        )}
        {!loading && products.length > 0 && (
          <div className="grid grid-cols-2 xl:grid-cols-3 gap-3">
            {products.map((p, idx) => (
              <ProductCard
                key={p.product_id}
                product={p}
                rank={config.mode === 'top_n' ? idx + 1 : undefined}
                inSet
                isExplicit={false}
                disabled
                onToggle={() => {}}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

/* ─── Mode Selector ─────────────────────────────────────────────────────── */

const MODES: { value: ProductSelectionMode; label: string; desc: string }[] = [
  { value: 'top_n', label: 'Top N', desc: 'Best performers by metric' },
  { value: 'explicit', label: 'Explicit', desc: 'Hand-pick specific products' },
  { value: 'rule_based', label: 'Rule-based', desc: 'Dynamic filter rules' },
];

function ModeSelector({
  mode,
  onChange,
}: {
  mode: ProductSelectionMode;
  onChange: (m: ProductSelectionMode) => void;
}) {
  return (
    <div className="grid grid-cols-3 gap-2">
      {MODES.map((m) => (
        <button
          key={m.value}
          onClick={() => onChange(m.value)}
          className={clsx(
            'flex flex-col items-start gap-0.5 px-3 py-2.5 rounded-xl border text-left transition',
            mode === m.value
              ? 'border-teal-500 bg-teal-50 ring-1 ring-teal-400'
              : 'border-slate-200 hover:border-slate-300 bg-white',
          )}
        >
          <span
            className={clsx(
              'text-xs font-bold',
              mode === m.value ? 'text-teal-700' : 'text-slate-700',
            )}
          >
            {m.label}
          </span>
          <span className="text-[10px] text-slate-500 leading-tight">{m.desc}</span>
        </button>
      ))}
    </div>
  );
}

/* ─── Top-N Config ──────────────────────────────────────────────────────── */

const SORT_OPTIONS: { value: ProductSortMetric; label: string }[] = [
  { value: 'revenue', label: 'Revenue' },
  { value: 'conversion_rate', label: 'Conversion Rate' },
  { value: 'views', label: 'Views' },
  { value: 'recency', label: 'Recency' },
];

const LOOKBACK_OPTIONS = [30, 60, 90, 180];

function TopNConfig({
  config,
  onChange,
}: {
  config: ProductConfiguration;
  onChange: (u: Partial<ProductConfiguration>) => void;
}) {
  const topN = config.top_n ?? 5;
  const sortBy: ProductSortMetric = config.sort_by ?? 'revenue';
  const lookback = config.lookback_days ?? 90;

  return (
    <div className="space-y-3">
      <div>
        <label className="text-xs font-semibold text-slate-600 block mb-1.5">
          Number of products
        </label>
        <div className="flex items-center gap-3">
          <input
            type="range"
            min={1}
            max={20}
            value={topN}
            onChange={(e) => onChange({ top_n: Number(e.target.value) })}
            className="flex-1 accent-teal-500"
          />
          <div className="flex items-center gap-1.5 shrink-0">
            <input
              type="number"
              min={1}
              max={50}
              value={topN}
              onChange={(e) => onChange({ top_n: Number(e.target.value) })}
              className="w-14 rounded-lg border border-slate-200 px-2 py-1 text-center text-sm font-bold"
            />
            <span className="text-xs text-slate-500">products</span>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-2">
        <div>
          <label className="text-xs font-semibold text-slate-600 block mb-1.5">
            Sort by
          </label>
          <select
            value={sortBy}
            onChange={(e) => onChange({ sort_by: e.target.value as ProductSortMetric })}
            className="w-full rounded-lg border border-slate-200 px-2 py-1.5 text-sm"
          >
            {SORT_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label className="text-xs font-semibold text-slate-600 block mb-1.5">
            Lookback
          </label>
          <select
            value={lookback}
            onChange={(e) => onChange({ lookback_days: Number(e.target.value) })}
            className="w-full rounded-lg border border-slate-200 px-2 py-1.5 text-sm"
          >
            {LOOKBACK_OPTIONS.map((d) => (
              <option key={d} value={d}>
                {d} days
              </option>
            ))}
          </select>
        </div>
      </div>
    </div>
  );
}

/* ─── Explicit Config ───────────────────────────────────────────────────── */

function ExplicitConfig({
  config,
  onChange,
  clientUid,
  orgUid,
}: {
  config: ProductConfiguration;
  onChange: (u: Partial<ProductConfiguration>) => void;
  clientUid: string;
  orgUid: string;
}) {
  const selectedIds = useMemo(() => new Set(config.product_ids ?? []), [config.product_ids]);
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<CatalogProduct[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedDetails, setSelectedDetails] = useState<Record<string, CatalogProduct>>({});
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      setLoading(true);
      productCatalogApi
        .searchProducts({ client_uid: clientUid, org_uid: orgUid, q: query || undefined, limit: 20, offset: 0 })
        .then((r) => {
          setResults(r.products);
          setSelectedDetails((prev) => {
            const next = { ...prev };
            for (const p of r.products) {
              if (selectedIds.has(p.product_id)) next[p.product_id] = p;
            }
            return next;
          });
        })
        .finally(() => setLoading(false));
    }, 300);
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query, clientUid, orgUid]);

  const toggle = (p: CatalogProduct) => {
    const next = new Set(config.product_ids ?? []);
    if (next.has(p.product_id)) {
      next.delete(p.product_id);
    } else {
      next.add(p.product_id);
      setSelectedDetails((prev) => ({ ...prev, [p.product_id]: p }));
    }
    onChange({ product_ids: Array.from(next) });
  };

  return (
    <div className="space-y-3">
      {selectedIds.size > 0 && (
        <div className="flex flex-wrap gap-1.5 p-2 bg-teal-50 rounded-lg border border-teal-100">
          {Array.from(selectedIds).map((id) => {
            const p = selectedDetails[id];
            return (
              <span
                key={id}
                className="inline-flex items-center gap-1 bg-white border border-teal-200 text-teal-700 rounded-full px-2 py-0.5 text-xs font-medium"
              >
                {p?.product_name ?? id.slice(0, 12)}
                <button onClick={() => toggle(p ?? { product_id: id } as CatalogProduct)}>
                  <X size={9} />
                </button>
              </span>
            );
          })}
        </div>
      )}

      <div className="relative">
        <Search size={13} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
        <input
          type="search"
          value={query}
          placeholder="Search to add products…"
          onChange={(e) => setQuery(e.target.value)}
          className="w-full rounded-lg border border-slate-200 pl-8 pr-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-teal-400"
        />
      </div>

      <div className="rounded-lg border border-slate-200 divide-y divide-slate-100 max-h-52 overflow-y-auto">
        {loading && (
          <div className="px-3 py-2 text-xs text-slate-500 flex items-center gap-2">
            <span className="w-3 h-3 border-2 border-slate-200 border-t-slate-500 rounded-full animate-spin" />
            Searching…
          </div>
        )}
        {!loading && results.length === 0 && (
          <div className="px-3 py-2 text-xs text-slate-400">No products found.</div>
        )}
        {results.map((p) => {
          const checked = selectedIds.has(p.product_id);
          return (
            <button
              key={p.product_id}
              type="button"
              onClick={() => toggle(p)}
              className="w-full flex items-center gap-2.5 px-3 py-2 text-left hover:bg-slate-50 transition"
            >
              <div
                className={clsx(
                  'w-4 h-4 rounded border flex items-center justify-center shrink-0',
                  checked ? 'bg-teal-500 border-teal-500' : 'border-slate-300',
                )}
              >
                {checked && <Check size={10} className="text-white" />}
              </div>
              {p.image_url ? (
                <img src={p.image_url} alt="" className="w-8 h-8 rounded object-cover bg-slate-100 shrink-0" />
              ) : (
                <div className="w-8 h-8 rounded bg-slate-100 shrink-0" />
              )}
              <div className="flex-1 min-w-0">
                <div className="text-sm font-medium truncate">{p.product_name}</div>
                <div className="text-[10px] text-slate-500">
                  {p.product_type ?? '—'}
                  {p.price != null ? ` · $${p.price.toFixed(2)}` : ''}
                </div>
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
}

/* ─── Rule-based Config ─────────────────────────────────────────────────── */

function RuleBasedConfig({
  config,
  onChange,
  clientUid,
  orgUid,
}: {
  config: ProductConfiguration;
  onChange: (u: Partial<ProductConfiguration>) => void;
  clientUid: string;
  orgUid: string;
}) {
  const rules: ProductFilterRules = config.rules ?? {};
  const [categories, setCategories] = useState<string[]>([]);
  const [tagInput, setTagInput] = useState('');

  useEffect(() => {
    productCatalogApi
      .listCategories(clientUid, orgUid)
      .then((r) => setCategories(r.categories))
      .catch(() => setCategories([]));
  }, [clientUid, orgUid]);

  const patchRules = (updates: Partial<ProductFilterRules>) => {
    const next = { ...rules, ...updates };
    const cleaned: ProductFilterRules = {};
    if (next.categories?.length) cleaned.categories = next.categories;
    if (next.tags?.length) cleaned.tags = next.tags;
    if (next.min_price != null) cleaned.min_price = next.min_price;
    if (next.max_price != null) cleaned.max_price = next.max_price;
    if (next.exclude_ids?.length) cleaned.exclude_ids = next.exclude_ids;
    onChange({ rules: cleaned });
  };

  const toggleCategory = (cat: string) => {
    const current = rules.categories ?? [];
    const next = current.includes(cat) ? current.filter((c) => c !== cat) : [...current, cat];
    patchRules({ categories: next });
  };

  const addTag = () => {
    const tag = tagInput.trim();
    if (!tag) return;
    const current = rules.tags ?? [];
    if (!current.includes(tag)) patchRules({ tags: [...current, tag] });
    setTagInput('');
  };

  const removeTag = (tag: string) => {
    patchRules({ tags: (rules.tags ?? []).filter((t) => t !== tag) });
  };

  const ruleCount = Object.keys(rules).filter(
    (k) => {
      const v = rules[k as keyof ProductFilterRules];
      return Array.isArray(v) ? v.length > 0 : v != null;
    },
  ).length;

  return (
    <div className="space-y-4">
      {ruleCount > 0 && (
        <div className="flex items-center gap-1.5 text-[10px] font-bold text-teal-600 bg-teal-50 border border-teal-200 rounded-lg px-2.5 py-1.5">
          <Check size={10} /> {ruleCount} rule{ruleCount > 1 ? 's' : ''} configured
        </div>
      )}

      <div>
        <div className="flex items-center gap-1.5 mb-2">
          <Tag size={12} className="text-slate-500" />
          <span className="text-xs font-semibold text-slate-700">Categories</span>
        </div>
        {categories.length === 0 ? (
          <p className="text-xs text-slate-400">Loading categories…</p>
        ) : (
          <div className="flex flex-wrap gap-1.5 max-h-32 overflow-y-auto">
            {categories.map((cat) => {
              const selected = (rules.categories ?? []).includes(cat);
              return (
                <button
                  key={cat}
                  onClick={() => toggleCategory(cat)}
                  className={clsx(
                    'inline-flex items-center gap-1 px-2 py-1 rounded-lg text-[11px] font-medium border transition',
                    selected
                      ? 'bg-teal-500 border-teal-500 text-white'
                      : 'bg-white border-slate-200 text-slate-600 hover:border-teal-300',
                  )}
                >
                  {selected && <Check size={9} />}
                  {cat}
                </button>
              );
            })}
          </div>
        )}
      </div>

      <div>
        <div className="flex items-center gap-1.5 mb-2">
          <TrendingUp size={12} className="text-slate-500" />
          <span className="text-xs font-semibold text-slate-700">Price Range</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="relative flex-1">
            <span className="absolute left-2.5 top-1/2 -translate-y-1/2 text-xs text-slate-400">$</span>
            <input
              type="number"
              min={0}
              value={rules.min_price ?? ''}
              placeholder="Min"
              onChange={(e) =>
                patchRules({ min_price: e.target.value ? Number(e.target.value) : undefined })
              }
              className="w-full rounded-lg border border-slate-200 pl-6 pr-2 py-1.5 text-sm focus:outline-none focus:ring-1 focus:ring-teal-400"
            />
          </div>
          <span className="text-slate-400 text-sm">–</span>
          <div className="relative flex-1">
            <span className="absolute left-2.5 top-1/2 -translate-y-1/2 text-xs text-slate-400">$</span>
            <input
              type="number"
              min={0}
              value={rules.max_price ?? ''}
              placeholder="Max"
              onChange={(e) =>
                patchRules({ max_price: e.target.value ? Number(e.target.value) : undefined })
              }
              className="w-full rounded-lg border border-slate-200 pl-6 pr-2 py-1.5 text-sm focus:outline-none focus:ring-1 focus:ring-teal-400"
            />
          </div>
        </div>
      </div>

      <div>
        <div className="flex items-center gap-1.5 mb-2">
          <BarChart2 size={12} className="text-slate-500" />
          <span className="text-xs font-semibold text-slate-700">Tags</span>
        </div>
        <div className="flex gap-2">
          <input
            type="text"
            value={tagInput}
            placeholder="Add tag…"
            onChange={(e) => setTagInput(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && addTag()}
            className="flex-1 rounded-lg border border-slate-200 px-3 py-1.5 text-sm focus:outline-none focus:ring-1 focus:ring-teal-400"
          />
          <button
            onClick={addTag}
            disabled={!tagInput.trim()}
            className="px-3 py-1.5 text-xs font-semibold bg-teal-500 text-white rounded-lg hover:bg-teal-600 disabled:opacity-40 transition"
          >
            Add
          </button>
        </div>
        {(rules.tags ?? []).length > 0 && (
          <div className="flex flex-wrap gap-1.5 mt-2">
            {(rules.tags ?? []).map((tag) => (
              <span
                key={tag}
                className="inline-flex items-center gap-1 bg-slate-100 text-slate-700 rounded-full px-2 py-0.5 text-[11px] font-medium"
              >
                {tag}
                <button onClick={() => removeTag(tag)} className="hover:text-red-500 transition">
                  <X size={9} />
                </button>
              </span>
            ))}
          </div>
        )}
      </div>

      <div>
        <div className="flex items-center gap-1.5 mb-2">
          <TrendingUp size={12} className="text-slate-500" />
          <span className="text-xs font-semibold text-slate-700">Sort matching products by</span>
        </div>
        <select
          value={config.sort_by ?? 'revenue'}
          onChange={(e) => onChange({ sort_by: e.target.value as ProductSortMetric })}
          className="w-full rounded-lg border border-slate-200 px-2 py-1.5 text-sm"
        >
          <option value="revenue">Revenue</option>
          <option value="conversion_rate">Conversion Rate</option>
          <option value="views">Views</option>
        </select>
      </div>
    </div>
  );
}

/* ─── Product Browser (Edit Mode right panel) ────────────────────────────── */

function ProductBrowser({
  config,
  onConfigChange,
  briefId,
  clientUid,
  orgUid,
}: {
  config: ProductConfiguration;
  onConfigChange: (c: ProductConfiguration) => void;
  briefId: string;
  clientUid: string;
  orgUid: string;
}) {
  const resolvedIds = useMemo(
    () => new Set(config.resolved_product_ids ?? []),
    [config.resolved_product_ids],
  );
  const explicitIds = useMemo(
    () => new Set(config.product_ids ?? []),
    [config.product_ids],
  );

  const isTopN = config.mode === 'top_n';
  const topN = config.top_n ?? 5;
  const sortBy = config.sort_by ?? 'revenue';

  const [query, setQuery] = useState('');
  const [allProducts, setAllProducts] = useState<CatalogProduct[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const fetchTopN = useCallback(async () => {
    setLoading(true);
    setLoadError(null);
    try {
      const r = await productCatalogApi.topNProducts({
        client_uid: clientUid,
        org_uid: orgUid,
        n: topN,
        sort_by: sortBy as 'revenue' | 'conversion_rate' | 'views',
      });
      setAllProducts(r.products);
    } catch (e) {
      setLoadError(String(e));
      setAllProducts([]);
    } finally {
      setLoading(false);
    }
  }, [clientUid, orgUid, topN, sortBy]);

  const fetchCatalog = useCallback(async (q?: string) => {
    setLoading(true);
    setLoadError(null);
    try {
      const r = await productCatalogApi.searchProducts({
        client_uid: clientUid,
        org_uid: orgUid,
        q: q || undefined,
        limit: 100,
        offset: 0,
      });
      setAllProducts(r.products);
    } catch (e) {
      setLoadError(String(e));
      setAllProducts([]);
    } finally {
      setLoading(false);
    }
  }, [clientUid, orgUid]);

  useEffect(() => {
    if (isTopN) {
      fetchTopN();
    } else {
      fetchCatalog();
    }
  }, [isTopN, fetchTopN, fetchCatalog]);

  const filteredProducts = useMemo(() => {
    if (isTopN || !query.trim()) return allProducts;
    const q = query.toLowerCase();
    return allProducts.filter(
      (p) =>
        p.product_name.toLowerCase().includes(q) ||
        (p.product_type ?? '').toLowerCase().includes(q),
    );
  }, [allProducts, query, isTopN]);

  const handleQueryChange = (q: string) => {
    setQuery(q);
    if (!isTopN) {
      if (debounceRef.current) clearTimeout(debounceRef.current);
      debounceRef.current = setTimeout(() => fetchCatalog(q || undefined), 350);
    }
  };

  const inSet = (p: CatalogProduct) =>
    resolvedIds.has(p.product_id) || explicitIds.has(p.product_id);

  const toggleExplicit = (p: CatalogProduct) => {
    const next = new Set(config.product_ids ?? []);
    if (next.has(p.product_id)) {
      next.delete(p.product_id);
    } else {
      next.add(p.product_id);
    }
    const nextConfig = { ...config, product_ids: Array.from(next), mode: 'explicit' as const };
    onConfigChange(nextConfig);
    generationApi.updateBrief(briefId, { product_config: nextConfig }).catch(() => {});
  };

  const inBrowserSet = isTopN ? [] : filteredProducts.filter(inSet);
  const notInBrowserSet = isTopN ? [] : filteredProducts.filter((p) => !inSet(p));

  return (
    <div className="flex flex-col overflow-hidden">
      {/* Header */}
      <div className="px-6 py-4 border-b border-slate-200 flex items-center gap-3">
        <div className="flex-1 min-w-0">
          <h3 className="text-sm font-bold text-slate-800 flex items-center gap-2">
            {isTopN ? (
              <>
                <TrendingUp size={14} className="text-teal-500" />
                Top {topN} by {SORT_LABEL[sortBy] ?? sortBy}
                <span className="text-xs font-normal text-slate-500">— live</span>
              </>
            ) : (
              <>
                Product Library
                {resolvedIds.size > 0 && (
                  <span className="text-xs font-normal text-slate-500">
                    {resolvedIds.size} resolved
                  </span>
                )}
              </>
            )}
          </h3>
          <p className="text-xs text-slate-500 mt-0.5">
            {isTopN
              ? 'Products ranked by real order data. Adjust N and sort metric on the left.'
              : 'Browse or search to preview and curate products.'}
          </p>
          <p className="text-[10px] text-slate-400 mt-0.5">
            Scope: <span className="font-mono">{clientUid}</span>
          </p>
        </div>
        {!isTopN && (
          <div className="relative shrink-0">
            <Search size={13} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <input
              type="search"
              value={query}
              placeholder="Filter products…"
              onChange={(e) => handleQueryChange(e.target.value)}
              className="rounded-lg border border-slate-200 pl-8 pr-3 py-1.5 text-sm w-48 focus:outline-none focus:ring-1 focus:ring-teal-400"
            />
          </div>
        )}
        {isTopN && (
          <button
            onClick={fetchTopN}
            disabled={loading}
            className="flex items-center gap-1.5 px-2.5 py-1.5 text-[11px] font-semibold text-teal-600 border border-teal-200 bg-teal-50 rounded-lg hover:bg-teal-100 disabled:opacity-50 transition"
          >
            <TrendingUp size={11} />
            Refresh
          </button>
        )}
      </div>

      <div className="flex-1 overflow-y-auto px-6 py-4 space-y-5">
        {loading && (
          <div className="flex items-center gap-2 text-sm text-slate-500 py-4">
            <span className="w-4 h-4 border-2 border-slate-200 border-t-teal-500 rounded-full animate-spin" />
            {isTopN ? `Ranking top ${topN} by ${SORT_LABEL[sortBy]}…` : 'Loading products…'}
          </div>
        )}

        {loadError && (
          <div className="text-xs text-red-700 bg-red-50 border border-red-200 rounded-lg px-3 py-2">
            {loadError}
          </div>
        )}

        {!loading && isTopN && filteredProducts.length > 0 && (
          <div className="grid grid-cols-2 xl:grid-cols-3 gap-3">
            {filteredProducts.map((p, idx) => (
              <ProductCard
                key={p.product_id}
                product={p}
                rank={idx + 1}
                inSet={inSet(p)}
                isExplicit={explicitIds.has(p.product_id)}
                onToggle={() => toggleExplicit(p)}
              />
            ))}
          </div>
        )}

        {!loading && !isTopN && inBrowserSet.length > 0 && (
          <div>
            <div className="flex items-center gap-2 mb-3">
              <span className="text-[10px] font-bold uppercase tracking-wider text-teal-600">Curated Set</span>
              <span className="text-[10px] bg-teal-100 text-teal-700 px-1.5 py-0.5 rounded-full font-bold">{inBrowserSet.length}</span>
            </div>
            <div className="grid grid-cols-2 xl:grid-cols-3 gap-3">
              {inBrowserSet.map((p) => (
                <ProductCard key={p.product_id} product={p} inSet isExplicit={explicitIds.has(p.product_id)} onToggle={() => toggleExplicit(p)} />
              ))}
            </div>
          </div>
        )}

        {!loading && !isTopN && notInBrowserSet.length > 0 && (
          <div>
            <div className="flex items-center gap-2 mb-3">
              <span className="text-[10px] font-bold uppercase tracking-wider text-slate-500">All Products</span>
              {query && <span className="text-[10px] text-slate-400">{notInBrowserSet.length} results</span>}
            </div>
            <div className="grid grid-cols-2 xl:grid-cols-3 gap-3">
              {notInBrowserSet.map((p) => (
                <ProductCard key={p.product_id} product={p} inSet={false} isExplicit={false} onToggle={() => toggleExplicit(p)} />
              ))}
            </div>
          </div>
        )}

        {!loading && !loadError && filteredProducts.length === 0 && (
          <div className="flex flex-col items-center justify-center py-12 text-slate-400">
            <Package size={28} className="mb-2 opacity-40" />
            {query.trim() ? (
              <p className="text-sm">No products match &ldquo;{query}&rdquo;.</p>
            ) : allProducts.length === 0 ? (
              <>
                <p className="text-sm font-medium">No products in catalog</p>
                <p className="text-xs mt-1 text-slate-400">Products for this client haven&apos;t been seeded yet.</p>
              </>
            ) : (
              <p className="text-sm">No products match your filter.</p>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

/* ─── Product Card ──────────────────────────────────────────────────────── */

function ProductCard({
  product,
  rank,
  inSet,
  isExplicit,
  disabled,
  onToggle,
}: {
  product: CatalogProduct;
  rank?: number;
  inSet: boolean;
  isExplicit: boolean;
  disabled?: boolean;
  onToggle: () => void;
}) {
  return (
    <div
      className={clsx(
        'relative rounded-xl border overflow-hidden transition group',
        inSet
          ? 'border-teal-300 ring-1 ring-teal-200 bg-white'
          : 'border-slate-200 bg-white hover:border-slate-300',
      )}
    >
      <div className="aspect-square bg-slate-100 overflow-hidden">
        {product.image_url ? (
          <img src={product.image_url} alt={product.product_name} className="w-full h-full object-cover" />
        ) : (
          <div className="w-full h-full flex items-center justify-center">
            <Package size={24} className="text-slate-300" />
          </div>
        )}
      </div>

      {rank != null && (
        <div className={clsx(
          'absolute top-2 left-2 w-5 h-5 rounded-full flex items-center justify-center text-[10px] font-black shadow',
          rank === 1 ? 'bg-amber-400 text-white' :
          rank === 2 ? 'bg-slate-400 text-white' :
          rank === 3 ? 'bg-amber-700 text-white' :
          'bg-slate-700/80 text-white',
        )}>
          {rank}
        </div>
      )}

      {rank == null && inSet && (
        <div className="absolute top-2 left-2 flex items-center gap-1 bg-teal-500 text-white text-[9px] font-bold px-1.5 py-0.5 rounded-full">
          <Check size={8} /> {isExplicit ? 'Selected' : 'Resolved'}
        </div>
      )}

      {!disabled && (
        <button
          onClick={onToggle}
          className={clsx(
            'absolute top-2 right-2 w-6 h-6 rounded-full flex items-center justify-center transition text-white',
            inSet
              ? 'bg-teal-500 opacity-100 hover:bg-red-400'
              : 'bg-slate-800/60 opacity-0 group-hover:opacity-100 hover:bg-teal-500',
          )}
          title={inSet ? 'Remove from selection' : 'Add to explicit selection'}
        >
          {inSet ? <X size={10} /> : <Check size={10} />}
        </button>
      )}

      <div className="p-2.5">
        <p className="text-xs font-semibold text-slate-800 truncate leading-tight">{product.product_name}</p>
        <p className="text-[10px] text-slate-500 mt-0.5">
          {product.product_type ?? 'Product'}
          {product.price != null ? ` · $${product.price.toFixed(2)}` : ''}
        </p>
      </div>
    </div>
  );
}
